import type { GridGeometry } from '../../grid';
import { getMapObjectVisualDefinition } from '../../content/objectVisualRegistry';
import { normalizeMapObjectPlacement } from '../../maps/mapObjectPlacement';
import { resolveTileDrawMetrics } from './board';
import { getMapObjectImage } from './objectImages';

export interface MapObjectSpriteInput {
    id: string;
    asset?: string;
    x: number;
    y: number;
    offsetX?: number;
    offsetY?: number;
    sizeMultiplier?: number;
    flipHorizontal?: boolean;
    flipVertical?: boolean;
    rotationSteps?: number;
}

export interface ResolvedMapObjectSpriteMetrics {
    image: HTMLImageElement;
    drawWidth: number;
    drawHeight: number;
    spriteCenterY: number;
    flipHorizontal: boolean;
    flipVertical: boolean;
    rotationSteps: number;
}

export function resolveMapObjectWorldAnchor(
    object: MapObjectSpriteInput,
    tileSize: number,
    grid: GridGeometry
): { x: number; y: number } {
    const normalized = normalizeMapObjectPlacement(object);
    const tileMetrics = resolveTileDrawMetrics(grid, tileSize, normalized.x, normalized.y);
    return {
        x: tileMetrics.centerX + normalized.offsetX * tileMetrics.spanWidth,
        y: tileMetrics.centerY + normalized.offsetY * tileMetrics.spanHeight,
    };
}

export function resolveMapObjectSpriteMetrics(
    object: MapObjectSpriteInput,
    tileSize: number
): ResolvedMapObjectSpriteMetrics | null {
    const visualId = object.asset ?? object.id;
    const definition = getMapObjectVisualDefinition(visualId);
    const image = getMapObjectImage(visualId);
    if (!definition || !image) {
        return null;
    }

    const maxDrawSize = tileSize * definition.drawScale * Math.max(0.01, object.sizeMultiplier ?? 1);
    const imageWidth = Math.max(1, image.naturalWidth || image.width || 1);
    const imageHeight = Math.max(1, image.naturalHeight || image.height || 1);
    const imageAspect = imageWidth / imageHeight;
    const drawWidth = imageAspect >= 1 ? maxDrawSize : maxDrawSize * imageAspect;
    const drawHeight = imageAspect >= 1 ? maxDrawSize / imageAspect : maxDrawSize;

    return {
        image,
        drawWidth,
        drawHeight,
        spriteCenterY: -((drawHeight / 2) + (definition.yOffsetTiles * tileSize)),
        flipHorizontal: Boolean(object.flipHorizontal),
        flipVertical: Boolean(object.flipVertical),
        rotationSteps: (((object.rotationSteps ?? 0) % 12) + 12) % 12,
    };
}

export function drawResolvedMapObjectSprite(
    ctx: CanvasRenderingContext2D,
    metrics: ResolvedMapObjectSpriteMetrics,
    anchorX: number,
    anchorY: number,
    alpha = 1
): void {
    ctx.save();
    ctx.globalAlpha *= alpha;
    ctx.imageSmoothingEnabled = false;
    ctx.translate(anchorX, anchorY + metrics.spriteCenterY);
    if (metrics.rotationSteps !== 0) {
        ctx.rotate(metrics.rotationSteps * (Math.PI / 6));
    }
    ctx.scale(metrics.flipHorizontal ? -1 : 1, metrics.flipVertical ? -1 : 1);
    ctx.drawImage(
        metrics.image,
        Math.round(-metrics.drawWidth / 2),
        Math.round(-metrics.drawHeight / 2),
        Math.round(metrics.drawWidth),
        Math.round(metrics.drawHeight),
    );
    ctx.restore();
}
